import type { RootState } from '@features/redux/store';
import { useSelector } from 'react-redux';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchOrgData, OrgUrls } from '@api/org';
import { Selection } from '../orgNavTypes';
import { Link } from 'react-router-dom';
import { Box, Link as MuiLink } from '@mui/material';
import type { OrgFilterInput } from '@components/OrgFilters';
import { ExportOrgs } from '@components/buttons/ExportOrgs';

const URL_MAP: Record<Selection.Company | Selection.Reseller | Selection.Location, OrgUrls> = {
    [Selection.Company]: OrgUrls.SearchCompanies,
    [Selection.Reseller]: OrgUrls.SearchResellers,
    [Selection.Location]: OrgUrls.SearchLocations,
} as const;

const ID_MAP: Record<Selection.Company | Selection.Reseller | Selection.Location, string> = {
    [Selection.Company]: 'companyId',
    [Selection.Reseller]: 'resellerId',
    [Selection.Location]: 'locationId',
} as const;

const TOTAL_MAP: Record<Selection.Company | Selection.Reseller | Selection.Location, string> = {
    [Selection.Company]: 'totalCompanies',
    [Selection.Reseller]: 'totalResellers',
    [Selection.Location]: 'totalLocations',
} as const;

const locationIdColumn = {
    field: 'locationId',
    headerName: 'Location ID',
    flex: 1,
    renderCell: (params: any) => (
        <Link to={`/manage/organizations?type=${Selection.Location}&id=${params.row.locationId}`}>{params.value}</Link>
    ),
};

const locationColumn = {
    field: 'locationName',
    headerName: 'Location',
    flex: 1,
    renderCell: (params: any) => (
        <Link to={`/manage/organizations?type=${Selection.Location}&id=${params.row.locationId}`}>{params.value}</Link>
    ),
};

const resellerColumn = {
    field: 'resellerName',
    headerName: 'Reseller',
    flex: 1,
    renderCell: (params: any) => (
        <Link to={`/manage/organizations?type=${Selection.Reseller}&id=${params.row.resellerId}`}>{params.value}</Link>
    ),
};

const companyColumn = {
    field: 'companyName',
    headerName: 'Company',
    flex: 1,
    renderCell: (params: any) => (
        <Link to={`/manage/organizations?type=${Selection.Company}&id=${params.row.companyId}`}>{params.value}</Link>
    ),
};

const numLocationsColumn = (onFilterChange?: (filters: OrgFilterInput, newTab: Selection) => void) => ({
    field: 'numLocations',
    headerName: '# Locations',
    flex: 1,
    renderCell: (params: any) => (
        <MuiLink
            component='button'
            sx={{ color: 'grey.800', textDecoration: 'underline' }}
            onClick={() => {
                const filters: OrgFilterInput = {
                    companies: {},
                    resellers: {},
                    locations: {},
                };

                if (params.row.companyId) {
                    filters.companies[params.row.companyId] = true;
                }
                if (params.row.resellerId) {
                    filters.resellers[params.row.resellerId] = true;
                }

                onFilterChange?.(filters, Selection.Location);
            }}>
            {params.value}
        </MuiLink>
    ),
});

const numResellersColumn = (onFilterChange?: (filters: OrgFilterInput, newTab: Selection) => void) => ({
    field: 'numResellers',
    headerName: '# Resellers',
    flex: 1,
    renderCell: (params: any) => (
        <MuiLink
            component='button'
            sx={{ color: 'grey.800', textDecoration: 'underline' }}
            onClick={() => {
                onFilterChange?.(
                    {
                        companies: { [params.row.companyId]: true },
                        resellers: {},
                        locations: {},
                    },
                    Selection.Reseller
                );
            }}>
            {params.value}
        </MuiLink>
    ),
});

const COLUMNS_MAP = (onFilterChange?: (filters: OrgFilterInput, newTab: Selection) => void) => ({
    [Selection.Company]: [companyColumn, numResellersColumn(onFilterChange), numLocationsColumn(onFilterChange)],
    [Selection.Reseller]: [resellerColumn, companyColumn, numLocationsColumn(onFilterChange)],
    [Selection.Location]: [locationIdColumn, locationColumn, resellerColumn, companyColumn],
});

interface OrgTableProps {
    orgFilters: {
        companyIds: string;
        resellerIds: string;
        locationIds: string;
    };
    tab: Selection.Company | Selection.Reseller | Selection.Location;
    onFilterChange?: (filters: OrgFilterInput, newTab: Selection) => void;
}

interface LocationSearchResponse {
    locationId: number;
    locationName: string;
    resellerName: string | null;
    resellerId: number | null;
    companyName: string;
    companyId: number;
    totalLocations: number;
}

interface ResellerSearchResponse {
    resellerId: number;
    resellerName: string;
    companyName: string;
    companyId: number;
    totalResellers: number;
}

interface CompanySearchResponse {
    companyId: number;
    companyName: string;
    totalCompanies: number;
}

export const OrgTable = ({ tab, orgFilters, onFilterChange }: OrgTableProps) => {
    const {
        IdToken,
        Roles: { role },
    } = useSelector(({ auth }: RootState) => auth);
    const tableRef = useRef<HTMLDivElement>(null);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 30,
    });

    const [loading, setLoading] = useState(false);

    const [data, setData] = useState<
        typeof tab extends Selection.Location
            ? LocationSearchResponse[]
            : typeof tab extends Selection.Reseller
            ? ResellerSearchResponse[]
            : CompanySearchResponse[]
    >([]);

    const fetchFn = useCallback(async () => {
        if (!role.locations.fullAccess && [Selection.Company, Selection.Reseller].includes(tab)) {
            setData([]);
            setLoading(false);
            return;
        }

        if (!role.resellers.fullAccess && Selection.Company === tab) {
            setData([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const data = await fetchOrgData<
            typeof tab extends Selection.Location
                ? LocationSearchResponse[]
                : typeof tab extends Selection.Reseller
                ? ResellerSearchResponse[]
                : CompanySearchResponse[]
        >(IdToken!, URL_MAP[tab], {
            pageNum: paginationModel.page,
            pageSize: paginationModel.pageSize,
            ...orgFilters,
        });

        setData(data);
        setLoading(false);

        // Scroll back to top when page changes
        // if (tableRef.current) {
        //     tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // }
    }, [IdToken, tab, orgFilters, paginationModel, role]);

    useEffect(() => {
        fetchFn();
    }, [fetchFn]);

    return (
        <Box
            display={'flex'}
            flexDirection={'column'}
            flex={1}
            gap={2}
            sx={{
                width: '100%',
                minWidth: 0,
            }}>
            <DataGridPro
                ref={tableRef}
                loading={loading}
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                pagination
                pageSizeOptions={[30]}
                paginationModel={paginationModel}
                paginationMode='server'
                rowCount={(data[0] as any)?.[TOTAL_MAP[tab]]}
                onPaginationModelChange={(model) => {
                    setPaginationModel(model);
                }}
                getRowId={(row: any) => `${row[ID_MAP[tab]]}-${tab}`}
                rows={data}
                columns={COLUMNS_MAP(onFilterChange)[tab]}
                sx={{
                    border: 'none',
                    backgroundColor: 'white',
                    color: 'grey.600',
                    fontSize: '0.875rem',
                    width: '100%',
                    minHeight: 'calc(100dvh - 180px) !important',
                    minWidth: 0,
                    flex: 1,
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: 'transparent',
                        fontWeight: 600,
                        borderBottom: '1px solid #e0e0e0',
                    },
                    '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid #f0f0f0',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                    },
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: '#fafafa',
                    },
                    '& .MuiDataGrid-footerContainer': {
                        borderTop: 'none',
                        backgroundColor: 'transparent',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        marginTop: '4px',
                    },
                }}
            />

            <Box display={'flex'} justifyContent={'flex-end'} pr={2} pb={2}>
                <ExportOrgs params={orgFilters} type={tab} />
            </Box>
        </Box>
    );
};
